import React, { useCallback } from 'react';
import { Card, Col } from 'react-bootstrap';
import { SkeletonTable } from '../../../components/Skeleton/Table';
import { useNamespacedTranslation } from '../../../../../hooks/useNamespacedTranslation';
import { SentShiftChangeRequestTableItem } from './mappings';
import { TableContentManual } from '../../../manager/components/TableContentManual';
import { CellProps, Column } from 'react-table';
import FormattedDateTime from '../../../../../components/datetime/FormattedDateTime';
import { EmployeeWithAvatar } from '../../../components/Employee';
import { ShiftDisplay } from '../SchedulerShiftsCalendar/ShiftDisplay';
import { getDetailedStatusBadgeColor } from '../SchedulerShiftsCalendar/shiftChangeRequestUtils';
import { ShiftChangeRequestApprovalStatus } from '../../../__generated__/graphql';
import ModalSidebar, {
  ModalSidebarContent,
} from '../../../components/ModalSidebar';
import { ShiftSidePanel } from '../SchedulerShiftsCalendar/ShiftSidePanel';
import EmptyStateImg from '../../../../../vendor/img/empty-state.png';
import { EmptyStateCardBody } from '../../../components/EmptyStateCard';
import {
  CancelRequestActionCell,
  CancelRequestModal,
} from './CancelRequestActionCell';
import { useCancelShiftChangeRequest } from '../SchedulerShiftsCalendar/useShiftChangeRequestActions';
import { useShiftChangeRequestSidebar } from '../SchedulerShiftsCalendar/useShiftChangeRequestSidebar';

const PAGE_SIZE = 50;

const i18Path = 'schedulerShifts.sentChangeRequests';
const i18PathStatus = 'schedulerShifts.shiftChangeRequest.detailedStatus';

type Props = {
  requests: SentShiftChangeRequestTableItem[];
  loading: boolean;
};

export const SentShiftChangeRequestsTable: React.FC<
  React.PropsWithChildren<Props>
> = ({ requests, loading }) => {
  const tStatus = useNamespacedTranslation(i18PathStatus);
  const t = useNamespacedTranslation(i18Path);
  const { selectedShift, handleShiftClick, handleCloseSidebar } =
    useShiftChangeRequestSidebar<SentShiftChangeRequestTableItem>({
      requests,
      loading,
    });
  const {
    requestToCancel,
    isCancelling,
    handleCancelClick,
    handleCancelConfirm,
    handleCancelClose,
  } = useCancelShiftChangeRequest();

  const actionsCell = useCallback(
    (props: CellProps<SentShiftChangeRequestTableItem>) => {
      const request = props.row.original;
      const canCancel =
        request.status === ShiftChangeRequestApprovalStatus.Pending;

      if (canCancel) {
        return (
          <CancelRequestActionCell
            requestId={request.id}
            requestToCancel={requestToCancel}
            isCancelling={isCancelling}
            onCancelClick={handleCancelClick}
          />
        );
      }
      return <Col></Col>;
    },
    [requestToCancel, isCancelling, handleCancelClick],
  );

  const columns: Column<SentShiftChangeRequestTableItem>[] = [
    {
      Header: t('table.header.shift'),
      accessor: 'shiftTitle',
      Cell: (props: CellProps<SentShiftChangeRequestTableItem>) => {
        const request = props.row.original;
        const shiftContent = (
          <ShiftDisplay
            title={request.shiftTitle}
            startDate={request.shiftStartDate}
            endDate={request.shiftEndDate}
          />
        );

        if (!request.shiftId) {
          return <Col>{shiftContent}</Col>;
        }

        return (
          <Col>
            <span
              className="link-primary link-underline-opacity-0 link-underline-opacity-100-hover"
              onClick={() => handleShiftClick(request)}
              role="button"
              tabIndex={0}>
              {shiftContent}
            </span>
          </Col>
        );
      },
    },
    {
      Header: t('table.header.proposedEmployee'),
      accessor: 'proposedEmployee',
      Cell: (props: CellProps<SentShiftChangeRequestTableItem>) => {
        const employee = props.row.original.proposedEmployee;
        if (!employee) {
          return <Col>-</Col>;
        }
        return (
          <Col>
            <EmployeeWithAvatar
              employee={{
                name: employee.name,
                avatarUrl: null,
              }}
            />
          </Col>
        );
      },
    },
    {
      Header: t('table.header.status'),
      accessor: 'statusDetailed',
      Cell: (props: CellProps<SentShiftChangeRequestTableItem>) => {
        const status = props.row.original.statusDetailed;
        if (!status) {
          return <Col>-</Col>;
        }
        return (
          <Col>
            <span className={`badge ${getDetailedStatusBadgeColor(status)}`}>
              {tStatus(`${status}`)}
            </span>
          </Col>
        );
      },
    },
    {
      Header: t('table.header.requestedAt'),
      accessor: 'requestedAt',
      Cell: (props: CellProps<SentShiftChangeRequestTableItem>) => (
        <Col>
          <FormattedDateTime
            dateTime={props.row.original.requestedAt}
            format="long"
          />
        </Col>
      ),
    },
    {
      Header: t('table.header.actions'),
      accessor: 'actions',
      Cell: actionsCell,
    },
  ];

  const renderTable = (requests: SentShiftChangeRequestTableItem[]) => {
    if (!requests.length) {
      return <EmptyStateCardBody title={t('emptyState')} img={EmptyStateImg} />;
    }
    return (
      <TableContentManual
        data={requests}
        columns={columns}
        onRowClick={() => {}}
        pageSize={PAGE_SIZE}
        itemsCount={requests.length}
        initialSortBy={[]}
        onSortByChanged={() => {}}
        initialPageIndex={0}
        onPageIndexChanged={() => {}}
      />
    );
  };

  return (
    <>
      <Card>
        {requests !== undefined && !loading ? (
          renderTable(requests)
        ) : (
          <SkeletonTable className="card-table" columns={5} rows={8} />
        )}
        <CancelRequestModal
          requestToCancel={requestToCancel}
          isCancelling={isCancelling}
          onConfirm={handleCancelConfirm}
          onClose={handleCancelClose}
        />
      </Card>
      {selectedShift && selectedShift.shiftId && (
        <ModalSidebar
          show={!!selectedShift}
          onClose={handleCloseSidebar}
          header={selectedShift.shiftTitle}
          content={
            <ModalSidebarContent
              body={<ShiftSidePanel shiftId={selectedShift.shiftId} />}
            />
          }
        />
      )}
    </>
  );
};
